import { useState } from "react";
import { useAuthActions } from "@convex-dev/auth/react";
import { useAction, useMutation } from "convex/react";
import { Lock } from "lucide-react";
import { api } from "../../../../convex/_generated/api";
import { Button } from "../ui/button";
import { errorMessage } from "../workspace/format";
import type { MeReady } from "../workspace/types";
import { LegalLinks, SITE_URL } from "./AuthScreen";

// Shown instead of the workspace when its Stripe subscription has ended. The
// server already refuses every other call (SUBSCRIPTION_INACTIVE); this screen
// just explains it and lets the owner re-check or restart.
export function SubscriptionLocked({ me }: { me: MeReady }) {
  const { signOut } = useAuthActions();
  const refresh = useAction(api.billing.refreshMine);
  const restart = useMutation(api.workspace.createFromGrant);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const owner = me.role === "owner";
  const grant = me.grant;

  const run = async (work: () => Promise<unknown>, done: string) => {
    setBusy(true); setError(null); setNotice(null);
    try { await work(); setNotice(done); }
    catch (caught) { setError(errorMessage(caught)); }
    finally { setBusy(false); }
  };

  return (
    <main className="auth-screen" id="main-content">
      <div className="auth-card">
        <div className="brand-mark" aria-label="Yodl"><span>Y</span></div>
        <h1><Lock size={18} /> {me.workspace.name} is paused</h1>
        <p className="auth-lede">
          {me.billing?.status === "unpaid" || me.billing?.status === "past_due"
            ? "The card on file could not be charged, so this workspace is locked. Nothing was deleted; messages and files come back the moment the subscription is active again."
            : "The Yodl subscription for this workspace has ended, so it is locked. Nothing was deleted; messages and files come back the moment the subscription is active again."}
        </p>
        {owner ? (
          grant !== undefined ? (
            <>
              <p className="auth-lede">A new Yodl plan for {me.user.email} is ready. Attach it to this workspace to unlock it.</p>
              <Button variant="primary" disabled={busy} onClick={() => { void run(() => restart({ displayName: me.displayName }), "Workspace unlocked."); }}>{busy ? "One moment…" : "Unlock with the new plan"}</Button>
            </>
          ) : (
            <>
              <a className="button button--primary" href={`${SITE_URL}/pricing/`}>Restart the plan at yodl.cc</a>
              <Button variant="ghost" disabled={busy} onClick={() => { void run(() => refresh({}), "Checked with Stripe."); }}>{busy ? "Checking…" : "I already paid, check again"}</Button>
              <p className="auth-lede">After checkout, open the link on the welcome page (or in the email) while signed in as {me.user.email} and the workspace unlocks.</p>
            </>
          )
        ) : (
          <p className="auth-lede">Ask your workspace owner to restart the plan at yodl.cc. You will be back in as soon as it is active.</p>
        )}
        {notice !== null ? <p className="form-notice" role="status">{notice}</p> : null}
        {error !== null ? <p className="form-error" role="alert">{error}</p> : null}
        <button type="button" className="link-button" onClick={() => { void signOut(); }}>Sign out</button>
        <LegalLinks />
      </div>
    </main>
  );
}
